import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "CCTV & Security Cameras Installation",
  "Access Control Systems",
  "Intercom & Video Doorbell Installation",
  "Structured Cabling",
  "Smart Light Switches & Smart Lock Installation",
  "Alarm & ULC Monitoring",
  "Home Theatre & Audio/Visual Systems",
  "TV Mounting",
  "WiFi Access Points",
  "Holiday & Permanent Lighting",
  "Multiple Services / Not Sure Yet",
];

const CONTACT = [
  { icon: Phone, label: "Call or text", value: "(506) 871-4144", href: "tel:+15068714144" },
  { icon: Mail, label: "Email", value: "info@safehomesecurity.ca", href: "mailto:info@safehomesecurity.ca" },
  { icon: MapPin, label: "Service area", value: "All of New Brunswick", href: null },
  { icon: Clock, label: "Hours", value: "Mon–Sun, 7:30am–7pm", href: null },
];

const CITIES = ["Moncton", "Saint John", "Fredericton", "Dieppe", "Woodstock", "Other"];

const EMPTY = { name: "", phone: "", email: "", city: "", service: "", message: "" };

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.city) {
      toast.error("Please select your city.");
      return;
    }
    if (!form.service) {
      toast.error("Please select a service you're interested in.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quotes`, form);
      toast.success("Quote request sent!", { description: data.message });
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(" ")
        : typeof detail === "string"
        ? detail
        : "Something went wrong. Please try again or call us directly.";
      toast.error("Could not send request", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-24 lg:py-32 overflow-x-clip">
      <div className="container-x grid lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ x: -24 }}
          whileInView={{ x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-brand-primary mb-4">
            Free Quote
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-zinc-900">
            Tell us what you need installed
          </h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="origin-left mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-[#8C6D1F] via-[#D4AF37] to-[#E9C767]"
          />
          <p className="mt-6 text-base leading-relaxed text-zinc-600 max-w-md">
            Send us a few details and we'll get back to you within one business day with a free,
            no-obligation quote. Prefer to talk? Call us anytime during business hours.
          </p>
          <div className="mt-10 space-y-6">
            {CONTACT.map(({ icon: Icon, label, value, href }) => (
              <div key={label} className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-md bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 font-semibold">{label}</p>
                  {href ? (
                    <a
                      href={href}
                      data-testid={`contact-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="text-base font-semibold text-zinc-900 hover:text-brand-primary transition-colors"
                    >
                      {value}
                    </a>
                  ) : (
                    <p className="text-base font-semibold text-zinc-900">{value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          data-testid="quote-form"
          onSubmit={handleSubmit}
          initial={{ x: 24 }}
          whileInView={{ x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="bg-white border border-zinc-200 rounded-md p-8 lg:p-10 shadow-sm"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="quote-name">Full Name *</Label>
              <Input
                id="quote-name"
                data-testid="quote-name-input"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="John Smith"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quote-phone">Phone *</Label>
              <Input
                id="quote-phone"
                data-testid="quote-phone-input"
                required
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="(506) 555-0000"
              />
            </div>
          </div>

          <div className="space-y-2 mt-6">
            <Label htmlFor="quote-email">Email *</Label>
            <Input
              id="quote-email"
              data-testid="quote-email-input"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="you@example.com"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mt-6">
            <div className="space-y-2">
              <Label>City *</Label>
              <Select value={form.city} onValueChange={(v) => setForm((f) => ({ ...f, city: v }))}>
                <SelectTrigger data-testid="quote-city-select">
                  <SelectValue placeholder="Select your city" />
                </SelectTrigger>
                <SelectContent>
                  {CITIES.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`quote-city-option-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Service Needed *</Label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger data-testid="quote-service-select">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`quote-service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2 mt-6">
            <Label htmlFor="quote-message">Project Details</Label>
            <Textarea
              id="quote-message"
              data-testid="quote-message-input"
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="e.g. 4 outdoor cameras for a two-storey home in Moncton…"
            />
          </div>

          <button
            type="submit"
            data-testid="quote-submit-btn"
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-brand-primary bg-brand-primary-hover text-black font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4AF37]/25 disabled:opacity-60 disabled:hover:translate-y-0"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "Sending…" : "Request My Free Quote"}
          </button>
          <p className="mt-4 text-xs text-zinc-500">
            No spam, no pressure. We'll only use your details to prepare your quote.
          </p>
        </motion.form>
      </div>
    </section>
  );
};
